import { computed, reactive, ref, watch } from 'vue'
import { DEFAULT_ADJUSTMENTS, DEFAULT_GOALS } from '../lib/defaults.js'
import { getBeneficiaryReminderPolicies, getDeathBenefitConfirmationPolicies, isBeneficiaryReminderPaused, mergePendingPolicies } from '../lib/beneficiary.js'
import { enrichPolicy, getNextConfirmedPaidYear, getPolicyCumulativePaid, getPolicyStatusInfo, getPolicyTotalPremium, paymentTimeline, policyTypeDistribution } from '../lib/calculations.js'
import { normalizePolicy } from '../lib/backup.js'
import { STORAGE_KEYS, setStorageErrorHandler, storage } from '../lib/storage.js'

const clone = (value) => JSON.parse(JSON.stringify(value))
const policies = ref([])
const gapGoals = ref(clone(DEFAULT_GOALS))
const gapAdjustments = ref(clone(DEFAULT_ADJUSTMENTS))
const selectedPerson = ref('all')
const searchQuery = ref('')
const typeFilter = ref('all')
const statusFilter = ref('all')
const viewMode = ref('list')
const notices = ref([])
const initialized = ref(false)

const addNotice = (message, tone = 'info') => {
  const id = `${Date.now()}_${Math.random().toString(36).slice(2)}`
  notices.value.push({ id, message, tone })
  setTimeout(() => { notices.value = notices.value.filter((notice) => notice.id !== id) }, 6000)
}

setStorageErrorHandler(({ operation }) => {
  addNotice(operation === 'write' ? '浏览器存储空间不足，最新修改可能未保存。请立即导出备份。' : '无法读取浏览器本地数据。', 'danger')
})

const init = () => {
  if (initialized.value) return
  const savedPolicies = storage.json(STORAGE_KEYS.policies, [])
  policies.value = Array.isArray(savedPolicies) ? savedPolicies.map(normalizePolicy) : []
  gapGoals.value = { ...DEFAULT_GOALS, ...(storage.json(STORAGE_KEYS.goals, {}) || {}) }
  const savedAdjustments = storage.json(STORAGE_KEYS.adjustments, {}) || {}
  gapAdjustments.value = Object.fromEntries(Object.keys(DEFAULT_ADJUSTMENTS).map((key) => [key, {
    excludedIds: Array.isArray(savedAdjustments[key]?.excludedIds) ? savedAdjustments[key].excludedIds : [],
    customItems: Array.isArray(savedAdjustments[key]?.customItems) ? savedAdjustments[key].customItems : [],
  }]))
  initialized.value = true
}

init()

watch(policies, (value) => storage.set(STORAGE_KEYS.policies, JSON.stringify(value)), { deep: true })
watch(gapGoals, (value) => storage.set(STORAGE_KEYS.goals, JSON.stringify(value)), { deep: true })
watch(gapAdjustments, (value) => storage.set(STORAGE_KEYS.adjustments, JSON.stringify(value)), { deep: true })

const people = computed(() => [...new Set(policies.value.flatMap((policy) => [policy.insuredPerson, policy.applicant]).filter(Boolean))])

const personPolicies = computed(() => selectedPerson.value === 'all'
  ? policies.value
  : policies.value.filter((policy) => [policy.insuredPerson, policy.applicant].includes(selectedPerson.value)))

const enrichedPolicies = computed(() => personPolicies.value.map((policy) => enrichPolicy(policy)))

const visiblePolicies = computed(() => {
  const query = searchQuery.value.trim().toLowerCase()
  return enrichedPolicies.value.filter((policy) => {
    const searchable = [policy.productName, policy.company, policy.applicant, policy.insuredPerson].join(' ').toLowerCase()
    const typeMatches = typeFilter.value === 'all' || policy.type === typeFilter.value
    const statusMatches = statusFilter.value === 'all' || policy.statusInfo.code === statusFilter.value ||
      (statusFilter.value === 'active' && !['lapsed', 'unknown'].includes(policy.statusInfo.code))
    return (!query || searchable.includes(query)) && typeMatches && statusMatches
  })
})

const activePolicies = computed(() => enrichedPolicies.value.filter((policy) => policy.statusInfo.code !== 'lapsed'))
const totalPaid = computed(() => personPolicies.value.reduce((sum, policy) => sum + getPolicyCumulativePaid(policy), 0))
const annualPremium = computed(() => activePolicies.value.reduce((sum, policy) => sum + getPolicyTotalPremium(policy), 0))
const reminders = computed(() => enrichedPolicies.value.filter((policy) => ['pending', 'grace', 'suspended', 'unknown'].includes(policy.statusInfo.code)).sort((a, b) => (a.statusInfo.diffDays ?? 99999) - (b.statusInfo.diffDays ?? 99999)))
const beneficiaryReminders = computed(() => getBeneficiaryReminderPolicies(enrichedPolicies.value))
const pausedBeneficiaryReminders = computed(() => policies.value.map((policy) => enrichPolicy(policy)).filter(isBeneficiaryReminderPaused))
const deathBenefitConfirmationReminders = computed(() => getDeathBenefitConfirmationPolicies(enrichedPolicies.value))
const pendingPolicies = computed(() => mergePendingPolicies(reminders.value, beneficiaryReminders.value, deathBenefitConfirmationReminders.value))
const timeline = computed(() => paymentTimeline(personPolicies.value))
const typeDistribution = computed(() => policyTypeDistribution(personPolicies.value))

const savePolicy = (input) => {
  const policy = normalizePolicy(input)
  if (!policy.id) policy.id = `pol_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`
  const index = policies.value.findIndex((item) => item.id === policy.id)
  if (index >= 0) policies.value[index] = policy
  else policies.value.unshift(policy)
  addNotice(index >= 0 ? '保单修改已保存。' : '保单已加入家庭档案。', 'success')
  return policy
}

const deletePolicy = (id) => {
  const target = policies.value.find((policy) => policy.id === id)
  if (!target) return false
  if (!confirm(`确定删除「${target.productName || '未命名保单'}」吗？此操作不可恢复。`)) return false
  policies.value = policies.value.filter((policy) => policy.id !== id)
  Object.values(gapAdjustments.value).forEach((adjustment) => {
    adjustment.excludedIds = adjustment.excludedIds.filter((itemId) => !itemId.startsWith(id))
  })
  addNotice('保单已删除。', 'success')
  return true
}

const deletePerson = (person) => {
  const related = policies.value.filter((policy) => [policy.insuredPerson, policy.applicant].includes(person))
  if (!related.length) return false
  if (!confirm(`确定删除与「${person}」相关的 ${related.length} 份保单吗？此操作不可恢复，请先导出备份。`)) return false
  const removedIds = new Set(related.map((policy) => policy.id))
  policies.value = policies.value.filter((policy) => !removedIds.has(policy.id))
  Object.values(gapAdjustments.value).forEach((adjustment) => {
    adjustment.excludedIds = adjustment.excludedIds.filter((itemId) => ![...removedIds].some((id) => itemId.startsWith(id)))
  })
  selectedPerson.value = 'all'
  addNotice(`已删除与 ${person} 相关的保单。`, 'success')
  return true
}

const markPaid = (id) => {
  const policy = policies.value.find((item) => item.id === id)
  if (!policy) return
  const nextPaidYear = getNextConfirmedPaidYear(policy)
  if (nextPaidYear === null) return addNotice('请先补充有效的保单生效日期。', 'warning')
  policy.lastPaidYear = nextPaidYear
  policy.statusOverride = 'auto'
  addNotice(`已记录缴费至 ${policy.lastPaidYear} 年。`, 'success')
}

const pauseBeneficiaryReminder = (id) => {
  const policy = policies.value.find((item) => item.id === id)
  if (!policy) return false
  policy.beneficiaryReminderPaused = true
  addNotice(`已暂停「${policy.productName || '未命名保单'}」的受益人变更提醒。`, 'success')
  return true
}

const resumeBeneficiaryReminder = (id) => {
  const policy = policies.value.find((item) => item.id === id)
  if (!policy) return false
  policy.beneficiaryReminderPaused = false
  addNotice(`已恢复「${policy.productName || '未命名保单'}」的受益人变更提醒。`, 'success')
  return true
}

const setImportedData = ({ policies: nextPolicies, goals, adjustments }) => {
  policies.value = nextPolicies.map(normalizePolicy)
  gapGoals.value = { ...DEFAULT_GOALS, ...goals }
  gapAdjustments.value = clone(adjustments)
  addNotice(`已载入 ${nextPolicies.length} 份保单。`, 'success')
}

const resetWorkspace = () => {
  if (!policies.value.length) return
  if (!confirm(`将清空 ${policies.value.length} 份保单、保障目标和校准项。请确认已经导出备份。`)) return
  policies.value = []
  gapGoals.value = clone(DEFAULT_GOALS)
  gapAdjustments.value = clone(DEFAULT_ADJUSTMENTS)
  selectedPerson.value = 'all'
  addNotice('本地看板已清空。', 'success')
}

export const policyStore = reactive({
  policies, gapGoals, gapAdjustments, selectedPerson, searchQuery, typeFilter, statusFilter, viewMode, notices,
  people, personPolicies, enrichedPolicies, visiblePolicies, activePolicies, totalPaid, annualPremium,
  reminders, beneficiaryReminders, pausedBeneficiaryReminders, deathBenefitConfirmationReminders, pendingPolicies, timeline, typeDistribution, addNotice, savePolicy, deletePolicy, deletePerson, markPaid, pauseBeneficiaryReminder, resumeBeneficiaryReminder, setImportedData,
  resetWorkspace, getPolicyStatusInfo,
})

export const usePolicyStore = () => policyStore
